import { hooks, state } from "@springtree/eva-sdk-react-recoil";
import { Core } from "@springtree/eva-services-core";
import { useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import { useTranslations } from "next-intl";

import { useConfiguration } from "~/contexts/configuration";
import useGiftcard from "~/hooks/use-giftcard";
import Constants from "~/utils/constants";
import evaUtils from "~/utils/eva-utils";
import intlUtils from "~/utils/intl-utils";

import FormattedAddress from "./formatted-address";
import Icon from "./icon";
import styles from "./order-info.module.scss";

type Props = {
  order: EVA.Core.OrderDto;
  amounts?: EVA.Core.OpenOrderAmounts;
  additionalOrderData?: EVA.Core.AdditionalOrderData;
  className?: string;
  cardClassName?: string;
  cardHeaderClassName?: string;
  cardContentClassName?: string;
};

export default function OrderInfo({
  order,
  amounts,
  additionalOrderData,
  className,
  cardClassName,
  cardContentClassName,
  cardHeaderClassName,
}: Props) {
  const t = useTranslations();
  const priceFormatter = intlUtils.usePriceFormatter();
  const { configuration } = useConfiguration();

  const evaEndpointUrl = hooks.useGetState(state.core.evaEndpointUrlState);
  const getOrganizationUnitSummary = hooks.useCallService({ service: Core.GetOrganizationUnitSummary });

  const products = order.Lines.filter((el) => el.ProductID && !el.IsCancelled);
  const firstShippingMethod = products?.find((line) => line.ShippingMethod && line.ShippingMethod.Name);
  const digitalGiftCards = products.filter((el) => el.IsDigitalGiftCard);

  const hasPudo = evaUtils.getCustomFieldValueForResource(Constants.PUDO_ID, order, configuration) as string;

  const storeInfo = useQuery({
    queryKey: ["getStoreInfo", order.PickupOrganizationUnitID],
    staleTime: Infinity,
    queryFn: async () => {
      const data = await getOrganizationUnitSummary({
        // @ts-expect-error
        OrganizationUnitID: `${order.PickupOrganizationUnitID}`,
      });

      if (!data) {
        return null;
      }

      return data;
    },
    enabled: !!evaEndpointUrl && !!order.PickupOrganizationUnitID,
  });

  const digitalGiftCardsQuery = useGiftcard().prepareGetDigitalGiftcardOptionsQuery(order || void 0);

  // Display the shipping address for delivery orders or pickup orders (with physical products)
  const showsShippingAddressCard =
    (!order.HasDelivery || order.ShippingAddress) && digitalGiftCards.length !== products.length;

  return (
    <div className={className}>
      {/*  show if not cancelled - shipping address (if present), shipping method (if present) and gift wrapping (if present) */}
      {order.TotalItems != 0 && (
        <div className={classNames(styles.card, cardClassName)}>
          <h2 className={cardHeaderClassName}>{t("generic.your_shipping_info")}</h2>
          <div className={classNames(styles.cardItems, cardContentClassName)}>
            {showsShippingAddressCard && (
              <div className={styles.cardItem}>
                <div className={styles.cardItemHeader}>
                  {order.HasDelivery ? (
                    hasPudo ? (
                      <>
                        <Icon name="pick-up" />
                        <h3>{t("generic.checkout.pickUpPoint")}</h3>
                      </>
                    ) : (
                      <>
                        <Icon name="store-locator" />
                        <h3>{t("generic.shipping_address")}</h3>
                      </>
                    )
                  ) : (
                    <>
                      <Icon name="pick-up" />
                      <h3>
                        {t(
                          order.SoldFromOrganizationUnitID == order.PickupOrganizationUnitID
                            ? "generic.purchasedAt"
                            : "generic.checkout.collectInStore"
                        )}
                      </h3>
                    </>
                  )}
                </div>
                <FormattedAddress address={order.HasDelivery ? order.ShippingAddress : storeInfo.data?.Address!} />
              </div>
            )}
            {digitalGiftCards.length > 0 && (
              <>
                {digitalGiftCards.length != products.length && <div className={styles.divider} aria-hidden />}
                <div className={styles.cardItem}>
                  <div className={styles.cardItemHeader}>
                    <Icon name="gift-card" />
                    <h3>{t("generic.shipping_method_giftcard")}</h3>
                  </div>
                  {digitalGiftCardsQuery?.data?.map((card, i) => (
                    <p key={i}>{card.Data.ToEmailAddress}</p>
                  ))}
                </div>
              </>
            )}
            {firstShippingMethod && (
              <>
                <div className={styles.divider} aria-hidden />
                <div className={styles.cardItem}>
                  <div className={styles.cardItemHeader}>
                    <Icon name="shipping" />
                    <h3>{t("generic.shipping_method")}</h3>
                  </div>
                  {firstShippingMethod.ShippingMethod!.Carrier.Name} {firstShippingMethod.ShippingMethod!.Name}
                </div>
              </>
            )}
            {additionalOrderData?.GiftWrappingOptions?.WrapOrder && (
              <>
                <div className={styles.divider} aria-hidden />
                <div className={styles.cardItem}>
                  <div className={styles.cardItemHeader}>
                    <Icon name="gift" />
                    <h3>{t("generic.gift_option.title")}</h3>
                  </div>
                  <p>
                    {t.rich("generic.thankyoupage.giftOption.standard", {
                      price: (chunks) => (
                        <span>
                          {priceFormatter.currency(amounts?.Types.GiftWrappingCosts.InTax || 0, {
                            currency: order.CurrencyID,
                          })}
                        </span>
                      ),
                    })}
                  </p>
                  {additionalOrderData?.GiftWrappingOptions?.Message && (
                    <p>
                      {t("generic.thankyoupage.gift_option.your_message")}
                      <br />
                      {additionalOrderData?.GiftWrappingOptions?.Message}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {/* billing e payment */}
      <div className={classNames(styles.card, cardClassName)}>
        <h2 className={cardHeaderClassName}>{t("generic.thankyoupage.gift_option.your_billing_payment")}</h2>
        <div className={classNames(styles.cardItems, cardContentClassName)}>
          <div className={styles.cardItem}>
            <div className={styles.cardItemHeader}>
              <Icon name="store-locator" />
              <h3>{t("generic.billing_address")}</h3>
            </div>
            <FormattedAddress address={order.BillingAddress} fiscalId={order.FiscalID} />
          </div>

          <div className={styles.divider} aria-hidden />
          <div className={styles.cardItem}>
            <div className={styles.cardItemHeader}>
              <Icon name={order.HasDelivery ? "wallet" : "cashier"} />
              <h3>{t("generic.payment_method")}</h3>
            </div>
            {order.Payments.length === 0 ? (
              order.HasDelivery ? (
                <p>{t("generic.cashOnDelivery")}</p>
              ) : (
                <p>{t("generic.payInStore")}</p>
              )
            ) : (
              order.Payments.map((paymentMethod, index) => (
                <p key={index} className={styles.paymentLabel}>
                  {order.Payments.length > 1
                    ? t("generic.paidWith", {
                        amount: priceFormatter.currency(paymentMethod.PaidAmount, { currency: order.CurrencyID }),
                        method: paymentMethod.PaymentName ?? paymentMethod.Description,
                      })
                    : paymentMethod.PaymentName ?? paymentMethod.Description}
                </p>
              ))
            )}
          </div>
        </div>
      </div>
      {/* fine blocco comune */}
    </div>
  );
}
